import React, { MouseEvent, forwardRef, useCallback, useMemo } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { MetricData, composeRefs } from '@rst/libs-ts'
import { useYM } from '@rst/libs-ts/src/metrics/ym'

import { Button_Shape } from '.'
import { LoadingSvg } from '../../organisms/loading-svg'
import { RstIcon, IconNameType } from '../icons'
import { ThemeType } from '../theme'
import { color, ColorsSchemeCoreNames, ColorsSchemeNames } from '../colors'
import { isDarkHex } from '../../libs'
import { typography } from '../typography'

export type Button2SizeType = 'small' | 'normal' | 'big'
export type Button2IconPositionType = 'left' | 'right'
export type Button2NativeType = 'button' | 'reset' | 'submit'

export interface Button2PropsType
	extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'onClick'> {
	icon?: IconNameType
	iconPosition?: Button2IconPositionType
	color?: ColorsSchemeCoreNames
	disabled?: boolean
	size?: Button2SizeType
	block?: boolean
	innerRef?:
		| React.Ref<HTMLButtonElement>
		| React.MutableRefObject<HTMLButtonElement>
	loading?: boolean
	type?: Button2NativeType
	light?: boolean
	square?: boolean
	disableColor?: boolean
	isTextBtn?: boolean
	shape?: Button_Shape
	transparent?: boolean
	onClick?: React.HTMLAttributes<HTMLButtonElement>['onClick'] | VoidFunction

	// Инфа для передачи статистики
	meta?: {
		url?: string
		type?: 'lead' | 'url' | 'openPollForm'
		text?: string
	}
}

export const Button2 = forwardRef<HTMLButtonElement, Button2PropsType>(
	(
		{
			color: _color = 'primary',
			size = 'normal',
			iconPosition = 'right',
			children,
			icon,
			block,
			innerRef,
			loading,
			type,
			onClick: _onClick,
			meta,
			...props
		},
		ref
	) => {
		const theme = useTheme()
		const isDarkBtn = useMemo(() => {
			const result = isDarkHex(color(_color)({ theme }) || '#f0f0f0')
			return result
		}, [_color, theme])

		const ym = useYM()

		const onClick = useCallback(
			(e: MouseEvent<HTMLButtonElement>) => {
				if (ym?.clickButton) {
					const data: MetricData = { size, color: _color }
					if (!meta?.text && typeof children === 'string') {
						data.text = children
					}
					if (meta?.text) {
						data.text = meta.text
					}

					if (meta?.type) {
						data.type = meta.type
					}

					if (meta?.url) {
						data.url = meta.url
					}
					ym?.clickButton({
						data,
					})
				}
				_onClick?.(e)
			},
			[_color, _onClick, children, meta?.text, meta?.type, meta?.url, size, ym]
		)

		const finalRef = useMemo(() => composeRefs(ref, innerRef), [innerRef, ref])

		return (
			<Button
				{...props}
				onClick={onClick}
				ref={finalRef}
				color={_color}
				size={size}
				block={block}
				$loading={loading}
				type={type}
				isDarkBtn={isDarkBtn}
			>
				{loading ? <StyleLoadingSvg size={24} /> : null}
				{icon && iconPosition === 'left' ? (
					<Icon type={icon} position={iconPosition} isChildren={!!children} />
				) : null}
				{children}
				{icon && iconPosition === 'right' ? (
					<Icon type={icon} position={iconPosition} isChildren={!!children} />
				) : null}
			</Button>
		)
	}
)

const preventGradient = (color: string) => {
	return `#${color.split('#')[1].slice(0, 7) || '000000'}`
}

const getColors = ({
	light,
	disabled,
	color: color_,
	disableColor,
	isTextBtn,
	isDarkBtn,
	transparent,
}: Button2PropsType & { isDarkBtn?: boolean }) => {
	if (disabled && isTextBtn)
		return css`
			color: ${color('gray-11')};
			pointer-events: none;
			background: transparent;
			border: none;
		`
	if (disabled)
		return css`
			color: ${color('gray-11')};
			background: ${color('gray-05')};
			border: 1px solid ${color('gray-06')};
			pointer-events: none;
		`
	if (disableColor)
		return css`
			background: ${color('gray-06')};
			border: 1px solid ${color('gray-07')};
			box-shadow: 0px 1px 0px rgba(27, 31, 35, 0.1);
			&:hover {
				background: ${color('gray-07')};
			}
			&:active {
				background: ${color('gray-07')};
			}
			&:focus {
				box-shadow: 0px 0px 0px 5px ${color('primary-04')};
			}
		`

	const hoverStyle = (c: ColorsSchemeNames, bg?: ColorsSchemeNames) => {
		if (light)
			return css`
				color: ${color(c)};
				background: ${bg ? color(bg) : null};
				border-color: ${color(c)};
			`
		if (isTextBtn)
			return css`
				color: ${color(c)};
			`
		else
			return css`
				background: ${color(c)};
				border-color: ${color(c)};
			`
	}
	const type = (() => {
		if (color_ === 'ghost' || color_ === 'dark') return 'gray'
		return color_
	})()

	const textColor = (() => {
		if (isTextBtn) return `${type}-08` as ColorsSchemeNames
		return light
			? (`${type}-08` as ColorsSchemeNames)
			: isDarkBtn
			? 'gray-04'
			: 'gray-19'
	})()

	if (color_ === 'dark')
		return css`
			color: ${color(textColor)};
			background: ${isTextBtn
				? 'transparent'
				: color(
						light
							? (`${type}-01` as ColorsSchemeNames)
							: (`${type}-19` as ColorsSchemeNames)
				  )};
			border: 1px solid
				${isTextBtn
					? 'transparent'
					: color(
							light
								? (`${type}-08` as ColorsSchemeNames)
								: (`${type}-19` as ColorsSchemeNames)
					  )};
			&:hover {
				${hoverStyle(`${type}-16` as ColorsSchemeNames)};
			}
			&:active {
				${hoverStyle(`${type}-15` as ColorsSchemeNames)};
			}
			&:focus {
				${hoverStyle(`${type}-15` as ColorsSchemeNames)};
			}
		`

	return css`
		color: ${({ theme }) => {
			const resultColor = color(textColor)({ theme })
			return resultColor?.startsWith('linear')
				? preventGradient(resultColor)
				: resultColor
		}};
		background: ${isTextBtn
			? 'transparent'
			: color(
					light
						? ((transparent
								? `opacity-${type}-01`
								: `${type}-01`) as ColorsSchemeNames)
						: (`${type}-08` as ColorsSchemeNames)
			  )};
		border: 0.4px solid
			${isTextBtn
				? 'transparent'
				: ({ theme }) => {
						const resultColor = color(
							(light ? `${type}-05` : `${type}-08`) as ColorsSchemeNames
						)({ theme })
						return resultColor?.startsWith('linear')
							? preventGradient(resultColor)
							: resultColor
				  }};
		&:hover {
			${hoverStyle(
				`${type}-09` as ColorsSchemeNames,
				(transparent ? `opacity-${type}-02` : `${type}-02`) as ColorsSchemeNames
			)};
		}
		&:active {
			${hoverStyle(
				`${type}-10` as ColorsSchemeNames,
				(transparent ? `opacity-${type}-03` : `${type}-03`) as ColorsSchemeNames
			)};
		}
		&:focus {
			${hoverStyle(
				`${type}-10` as ColorsSchemeNames,
				(transparent ? `opacity-${type}-03` : `${type}-03`) as ColorsSchemeNames
			)};
		}
	`
}

const getFontSize = ({ size }: Button2PropsType) => {
	if (size === 'big')
		return css`
			${typography.head3.bold}
		`
	if (size === 'small') return typography.subheadline.medium
	return typography.body.bold
}

const getSize = ({ size, block, square, isTextBtn }: Button2PropsType) => {
	if (isTextBtn) return ''
	const sizeValue = (() => {
		if (size === 'big') return '56px'
		if (size === 'small') return '40px'
		return '48px'
	})()
	return css`
		height: ${sizeValue};
		min-width: ${sizeValue};
		padding: ${() => {
			if (size === 'big') return '14px 32px'
			if (size === 'small') return '8px 16px'
			return '12px 24px'
		}};
		${() => {
			if (block) return `width: 100%`
			return ''
		}}
		${() => {
			if (square)
				return css`
					padding: 0;
					width: ${sizeValue};
				`
			return ''
		}}
	`
}

const shape = css`
	${({ shape, theme }: { theme: ThemeType; shape: Button_Shape }) => {
		if (shape === 'square' || theme.buttonShape === 'square') {
			return css`
				&& {
					border-radius: 0;
				}
			`
		}
		if (shape === 'round' || theme.buttonShape === 'round') {
			return css`
				&& {
					border-radius: 35px;
				}
			`
		}
		return ''
	}}
`

const loading = ({ $loading }: { $loading?: boolean }) => {
	if (!$loading) return ''
	return css<Button2PropsType>`
		overflow: hidden;
		pointer-events: none;
	`
}

const Button = styled.button<
	Button2PropsType & { isDarkBtn?: boolean; $loading?: boolean }
>`
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	transition: 0.1s background-color, 0.1s color, 0.1s box-shadow;
	border-radius: 12px;
	outline: none;
	position: relative;
	font-family: ${({ theme }) => theme?.font?.main || 'Inter'}, Arial;
	font-stretch: ${({ theme }) => theme?.fontStretch || 'normal'};
	${getColors};
	${getSize};
	${getFontSize};
	${loading};
	${shape};
`

type IconStyledType = {
	position: Button2IconPositionType
	size: Button2SizeType
	isChildren?: boolean
}

const getStyledIcon = ({ size, position, isChildren }: IconStyledType) => {
	if (!isChildren) return ''
	const sizeMargin = size === 'small' ? '6.5px' : '12px'
	if (position === 'left')
		return css`
			margin-right: ${sizeMargin};
		`
	return css`
		margin-left: ${sizeMargin};
	`
}

const Icon = styled(RstIcon)<IconStyledType>`
	display: flex;
	justify-content: center;
	align-items: center;
	color: inherit;
	font-size: inherit;
	transition: 0.1s color;
	${getStyledIcon};
`

const StyleLoadingSvg = styled(LoadingSvg)`
	position: absolute;
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	background: ${color('primary-01')};
	left: 0;
	top: 0;
`
